/**
 * Auth Guard — protects routes requiring authentication.
 *
 * Flow depends on auth mode:
 *   - Bearer (Teams / add-in): cookie check → tryRefreshSession
 *     (issueNewToken from localStorage refresh-token) → redirect to login.
 *   - Cookie (browser + Tauri): cookie check → login handoff fragment, if
 *     login-web left one → SessionService.refreshSession (issueNewCookieToken)
 *     → redirect to SIGN IN, bounded by `AuthRecoveryService`.
 *
 * Uses environment.SPACES_CLIENT_ID (never hardcoded) for auth handshake.
 *
 * AI CONTEXT: This guard runs before any component loads. Bearer surfaces
 * store refresh-token / tenant in localStorage. Browser and Tauri rely on
 * cookies as the source of auth and never refresh via stored refresh-token.
 * Tauri cookie refresh via the HTTP plugin is intended but not fully verified.
 *
 * A BOOT FAILURE MUST NEVER CALL `logout()`. login-web's `/logout` clears the
 * session cookies, so doing it here deletes the credential sign-in has just
 * minted and asks login-web to mint another — which is a loop, not a recovery,
 * and it shows a user who is trying to get in a screen that says "signing out".
 * Boot failures go to login and are counted; see `auth-recovery.ts`.
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import axios from 'axios';
import { environment } from '../../../environments/environment';
import {
  clearPendingLoginHandoff,
  loginReturnPath,
  pendingLoginHandoff,
} from '../utils/login-return-path';
import { extractSlugFromHostname, isCustomDomainHost } from '../utils/tenant-host';
import {
  parseSignageHandoffHash,
  signageHandoffDedupeKey,
} from '../../features/signage/kernel/signage-handoff';
import { usesBearerTokenAuth } from '../auth-mode';
import { TeamsService } from '../services/teams.service';
import { TeamsAuthService } from '../services/teams-auth.service';
import { SessionService } from '../services/session.service';
import { AuthRecoveryService } from '../services/auth-recovery.service';
import { SIGN_IN_PROBLEM_ROUTE, type SignInFailureReason } from '../services/auth-recovery';
import { isAdminPreview } from './admin-preview';
import { getApiUrl, getLoginUrl, regionGateActive } from '../region';

const SPACES_CLIENT = environment.SPACES_CLIENT_ID;

const REFRESH_TOKEN_KEY = 'refresh-token';
const TENANT_KEY = 'tenant';
const ACCESS_TOKEN_KEY = 'access-token';

/**
 * Auth guard ported from visitor-management-web.
 * Dual-mode: bearer token refresh vs cookie refresh (browser).
 */
export const authGuard: CanActivateFn = async (route) => {
  // Data-residency gate (desktop): until the user picks a region on the welcome
  // screen, make NO backend call to any region. app.ts renders the welcome
  // surface over everything, so blocking here is safe.
  if (regionGateActive()) return false;

  // Dev-only offline preview of the admin shell (see admin-preview.ts).
  if (isAdminPreview(route)) return admit(inject(SessionService));

  // Test-harness bypass — when the URL carries `?testHarness=1` OR the
  // window has `__schedulePreviewMode = true`, treat the user as
  // authenticated. Lets Playwright specs that don't depend on viewer-
  // scoped data render the routed component without going through the
  // login redirect cascade. Gated to non-production (mirrors
  // admin-preview.ts) so the bypass can NEVER grant route access in a
  // production build via `?testHarness=1` (security audit P0 / CWE-287).
  try {
    if (!environment.production && typeof window !== 'undefined') {
      const w = window as { __schedulePreviewMode?: boolean };
      const params = new URLSearchParams(window.location.search);
      if (params.get('testHarness') === '1' || w.__schedulePreviewMode) {
        return admit(inject(SessionService));
      }
    }
  } catch {
    /* fall through to the normal flow */
  }

  // Teams: wait for SSO to finish before checking tokens. The APP_INITIALIZER
  // runs async — without this wait, the guard fires before the token is stored.
  // Inject SessionService before any await so the browser path stays in context.
  const teamsService = inject(TeamsService);
  const session = inject(SessionService);
  const recovery = inject(AuthRecoveryService);
  const router = inject(Router);
  if (teamsService.isInTeams()) {
    const teamsAuth = inject(TeamsAuthService);
    await teamsAuth.waitForInit();
    const authenticated = teamsAuth.isAuthenticated() || !!localStorage.getItem('access-token');
    return authenticated ? admit(session) : false;
  }

  const bearer = usesBearerTokenAuth(teamsService.isInTeams());
  const handoff = pendingLoginHandoff();

  if (bearer) {
    bootstrapTokensFromHash();
  } else if (hasLoginHandoff()) {
    // An explicit sign-in return selects its identity even when another account's
    // cookie is still valid. Consume it before probing the session, and always
    // remove the credential fragment. A failed exchange must not silently show
    // the previous account as though the requested sign-in succeeded.
    if (!(await adoptSessionFromHash())) {
      return router.parseUrl(SIGN_IN_PROBLEM_ROUTE);
    }
  }

  const ok = await hasValidCookieSession();

  if (ok) {
    session.markSessionEstablished();
    // The shell can have started a viewer request under the previous cookie.
    // A full same-origin navigation discards that service/Apollo graph before
    // entering the new identity, and prevents Angular restoring the old fragment.
    if (handoff) {
      window.location.replace(window.location.origin + window.location.pathname + window.location.search + handoff.routeHash);
      return false;
    }
    return admit(session);
  }

  if (bearer) {
    const refreshed = await tryRefreshSession();
    if (refreshed) return admit(session);
    redirectToLogin();
    return false;
  }

  // Cookie path (browser + Tauri): never use stored refresh-token.
  const outcome = await session.refreshSession();
  if (outcome === 'refreshed') {
    // SessionService owns the stamp because it knows whether this was a real
    // server refresh (proven) or a sibling tab's recent shared-cookie refresh
    // (established historically, but not proof for this document).
    return admit(session);
  }

  if (outcome === 'unavailable' && session.hasEstablishedSession()) {
    // The renewal reached no verdict — rate-limited, gateway, or offline (see
    // `session-refresh.ts`). The cookie was NOT rejected, so tearing the
    // session down here would trade a transient backend condition for a full
    // SSO round-trip, and login-web's own token mint spends the same
    // rate-limit bucket that may have caused this.
    //
    // This browser has held a session, so let the app render: it comes up on
    // the persisted Apollo cache with the connection banner showing, and if the
    // session really is dead the first real query 401s and the ErrorLink ejects
    // them properly.
    return admit(session);
  }

  // Everything else is one thing: we could not establish a session, so the user
  // needs to sign in. `logout()` is NOT that — it would clear the cookies
  // sign-in just set (see the file docblock). If this browser has already made
  // the round trip and come back no better, stop and explain instead.
  const reason: SignInFailureReason =
    outcome === 'expired' ? 'credential-rejected' : 'refresh-unavailable';
  if (recovery.noteFailedSignIn(reason)) {
    return router.parseUrl(SIGN_IN_PROBLEM_ROUTE);
  }
  redirectToLogin();
  return false;
};

/**
 * Let the navigation through, and tell the shell it may now load the viewer.
 *
 * Every `true` this guard returns goes through here. The shell starts its
 * identity-scoped reads only on admission (`SessionService.sessionAdmitted`),
 * so a boot the guard sends back to sign-in makes none of them. That signal
 * explains what those reads used to cost.
 */
function admit(session: SessionService): true {
  session.admitSession();
  return true;
}

/** Whether login-web left a refresh token in this URL's fragment. */
function hasLoginHandoff(): boolean {
  return !!pendingLoginHandoff();
}

/**
 * Check if the browser already has a valid session cookie.
 * Retries once on transient errors (5xx, network) to avoid
 * unnecessary login redirects during brief API hiccups.
 */
async function hasValidCookieSession(): Promise<boolean> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const res = await axios.post(
        getApiUrl(),
        { query: `query viewer { viewer { _id } }` },
        {
          withCredentials: true,
          headers: { spaces_client: SPACES_CLIENT },
          timeout: 8000,
        },
      );
      return !!res?.data?.data?.viewer?._id;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // 401/403 = definitely not authenticated — don't retry
      if (status === 401 || status === 403) return false;
      // 5xx or network error on first attempt — retry after a short delay
      if (attempt === 0) {
        await new Promise(r => setTimeout(r, 1000));
        continue;
      }
      return false;
    }
  }
  return false;
}

/** Redirect to login-web with app/path/slug/customer params */
function redirectToLogin(): void {
  // Sign-in always happens on the platform login host, including for branded
  // tenants. That host is the only origin registered with Microsoft as a
  // redirect URI; a per-tenant login host would need a registration each, and
  // Entra caps those at 256 and cannot raise it. The slug below carries the
  // tenant so login-web still renders their branding and returns them here.
  const loginUrl = getLoginUrl() ?? '/login';
  const sep = loginUrl.includes('?') ? '&' : '?';
  const app = environment.APP;
  // Preserve hash-routed QR/deep-link intent through login.
  const path = loginReturnPath(window.location);
  const encodedApp = encodeURIComponent(app ?? '');
  const encodedPath = encodeURIComponent(path);
  let url = `${loginUrl}${sep}app=${encodedApp}&path=${encodedPath}`;

  // Pass slug from hostname so login-web can load tenant branding
  const slug = extractSlugFromHostname();
  if (slug) {
    url += `&slug=${encodeURIComponent(slug)}`;
  } else if (isCustomDomainHost()) {
    // A customer domain has no slug to send, so name the host instead. It is
    // what tells login-web which division to brand as and, more importantly,
    // where to send the user back to — resolving by their email domain would
    // pick whichever division owns that domain, not the one this host serves.
    url += `&domain=${encodeURIComponent(window.location.hostname)}`;
  }

  // Fallback: existing customerId from localStorage (only if no slug)
  try {
    const customerId = localStorage.getItem('customer-id');
    if (customerId && !slug) {
      url += `&customer=${encodeURIComponent(customerId)}`;
    }
  } catch { /* ignore */ }
  const handoff = parseSignageHandoffHash(window.location.hash);
  if (handoff) {
    try {
      sessionStorage.setItem(
        `${signageHandoffDedupeKey(handoff)}:auth-pending`,
        '1',
      );
    } catch {
      /* storage unavailable — timestamp dedupe remains */
    }
  }
  window.location.href = url;
}

/**
 * Trade the login handoff fragment for first-party cookies on this host.
 *
 * login-web puts the refresh token in the fragment of every post-login
 * redirect. On our own hosts nothing reads it, because the cookie already
 * travelled; on a customer's domain it is the only thing that crossed the
 * boundary, since a cross-origin Set-Cookie for this host would be a
 * third-party write and browsers drop it.
 *
 * Deliberately not the bearer path: nothing is written to localStorage, the
 * session stays cookie based, and the fragment is wiped either way so a
 * long-lived refresh token does not sit in the address bar and history.
 *
 * The exchange is only as good as where `getApiUrl()` points. Same-origin
 * `/graphql` — or any host under the same registrable domain, which is what our
 * own hosts are — gets a Set-Cookie the browser keeps. A customer domain whose
 * build still names the platform API absolutely is asking for a cross-site
 * cookie, and Safari will not keep it however many times we retry; that is one
 * of the facts `/sign-in-problem` reports rather than looping over.
 */
async function adoptSessionFromHash(): Promise<boolean> {
  const parsed = pendingLoginHandoff();
  // Spent or refused below, either way: a second guard run must not replay it.
  clearPendingLoginHandoff();
  if (!parsed) return false;

  const { refreshToken, tenant, routeHash } = parsed;
  if (refreshToken.length > 4096 || tenant.length > 256) return false;
  if (/[<>"']/.test(refreshToken) || /[<>"']/.test(tenant)) return false;

  let adopted = false;
  try {
    const res = await axios.post(
      getApiUrl(),
      {
        query: `mutation issueNewToken($refreshToken: String!, $tenant: String!) {
          issueNewToken(refreshToken: $refreshToken, tenant: $tenant, useCookies: true) {
            idToken
          }
        }`,
        variables: { refreshToken, tenant },
      },
      { withCredentials: true, headers: { spaces_client: SPACES_CLIENT } },
    );
    adopted = !!res?.data?.data?.issueNewToken?.idToken;
  } catch {
    adopted = false;
  }

  // Wipe the fragment even on failure — a token we could not spend is still a
  // token, and leaving it in history buys nothing.
  try {
    history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + routeHash,
    );
  } catch {
    /* history unavailable — the exchange result still stands */
  }
  return adopted;
}

/** Extract refresh token + tenant from URL hash fragment (passed by login-web).
 *  Bearer surfaces only (Teams / add-in) — browser and Tauri use cookies. */
function bootstrapTokensFromHash(): void {
  const parsed = pendingLoginHandoff();
  clearPendingLoginHandoff();
  if (parsed) {
    const { refreshToken: rt, tenant: t, routeHash } = parsed;
    if (rt.length > 4096 || t.length > 256) return;
    if (/[<>"']/.test(rt) || /[<>"']/.test(t)) return;

    localStorage.setItem(REFRESH_TOKEN_KEY, rt);
    localStorage.setItem(TENANT_KEY, t);
    history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search + routeHash,
    );
  }
}

/**
 * Attempt to refresh the session using stored refresh token (bearer surfaces).
 *
 * Exported so `adminGuard` can re-use the exact same path. Browser and Tauri
 * must use SessionService.refreshAccessToken instead (cookie session).
 */
export async function tryRefreshSession(): Promise<boolean> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const tenantId = localStorage.getItem(TENANT_KEY);
  if (!refreshToken || !tenantId) return false;

  try {
    const res = await axios.post(
      getApiUrl(),
      {
        query: `mutation issueNewToken($refreshToken: String!, $tenant: String!) {
          issueNewToken(refreshToken: $refreshToken, tenant: $tenant, useCookies: true) {
            idToken
            refreshToken
            identityTenant
          }
        }`,
        variables: { refreshToken, tenant: tenantId },
      },
      {
        withCredentials: true,
        headers: { spaces_client: SPACES_CLIENT },
      },
    );

    const token = res?.data?.data?.issueNewToken;
    if (!token?.idToken) return false;

    localStorage.setItem(ACCESS_TOKEN_KEY, token.idToken);
    if (token.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, token.refreshToken);
    if (token.identityTenant) localStorage.setItem(TENANT_KEY, token.identityTenant);
    return true;
  } catch {
    return false;
  }
}
